import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import api from '../../services/api';

const SettingsManager = () => {
    const { settings, fetchSettings, showToast, articles, videos, fetchArticles, fetchVideos } = useApp();
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [deleteType, setDeleteType] = useState('all');
    const [deleteVideoType, setDeleteVideoType] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [deleteCategory, setDeleteCategory] = useState('');
    const [deleteStatus, setDeleteStatus] = useState('');
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [password, setPassword] = useState('');
    const [pendingAction, setPendingAction] = useState(null);
    const [passwordError, setPasswordError] = useState('');

    const [showChangePassword, setShowChangePassword] = useState(false);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    useEffect(() => {
        setFormData(settings);
    }, [settings]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSave = async () => {
        try {
            await api.put('/settings', formData);
            await fetchSettings();
            showToast('Settings saved successfully');
        } catch (error) {
            showToast('Error saving settings', 'error');
        }
    };

    // Verify admin password
    // In the verifyPassword function, add email
    const verifyPassword = async (action) => {
        setPasswordError('');

        try {
            // Get admin email from settings or use default
            const adminEmail = settings?.email || 'admin@wistarnews.com';

            const response = await api.post('/auth/verify-password', {
                password,
                email: adminEmail
            });

            if (response.data.valid) {
                setShowPasswordModal(false);
                setPassword('');
                await action();
            } else {
                setPasswordError('Invalid password. Please try again.');
            }
        } catch (error) {
            setPasswordError('Error verifying password. Please try again.');
        }
    };

    const handleChangePassword = async () => {
        if (newPassword !== confirmPassword) {
            showToast('New passwords do not match', 'error');
            return;
        }

        if (newPassword.length < 6) {
            showToast('Password must be at least 6 characters', 'error');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/auth/change-password', {
                email: settings?.email || 'admin@wistarnews.com',
                oldPassword,
                newPassword
            });

            if (response.data.success) {
                showToast('Password changed successfully');
                setShowChangePassword(false);
                setOldPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                showToast(response.data.message, 'error');
            }
        } catch (error) {
            showToast('Error changing password', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Delete All Articles
    const deleteAllArticles = async () => {
        setLoading(true);
        try {
            await api.post('/articles/bulk-delete', { ids: [] });
            await fetchArticles();
            showToast('✅ All articles deleted successfully');
        } catch (error) {
            showToast('Error deleting articles', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Delete All Videos
    const deleteAllVideos = async () => {
        setLoading(true);
        try {
            await api.post('/videos/bulk-delete', { ids: [] });
            await fetchVideos();
            showToast('✅ All videos deleted successfully');
        } catch (error) {
            showToast('Error deleting videos', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Delete Articles by Date Range
    const deleteArticlesByDate = async () => {
        if (!dateFrom || !dateTo) {
            showToast('Please select both start and end dates', 'error');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/articles/bulk-delete-by-date', {
                dateFrom,
                dateTo
            });
            await fetchArticles();
            showToast(`✅ ${response.data.deletedCount} articles deleted`);
            setDateFrom('');
            setDateTo('');
        } catch (error) {
            showToast('Error deleting articles by date', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Delete Articles by Category
    const deleteArticlesByCategory = async () => {
        if (!deleteCategory) {
            showToast('Please select a category', 'error');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/articles/bulk-delete-by-category', {
                category: deleteCategory
            });
            await fetchArticles();
            showToast(`✅ ${response.data.deletedCount} articles deleted from ${deleteCategory}`);
            setDeleteCategory('');
        } catch (error) {
            showToast('Error deleting articles by category', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Delete Articles by Status
    const deleteArticlesByStatus = async () => {
        if (!deleteStatus) {
            showToast('Please select a status', 'error');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/articles/bulk-delete-by-status', {
                status: deleteStatus
            });
            await fetchArticles();
            showToast(`✅ ${response.data.deletedCount} ${deleteStatus} articles deleted`);
            setDeleteStatus('');
        } catch (error) {
            showToast('Error deleting articles by status', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Delete Videos by Date Range
    const deleteVideosByDate = async () => {
        if (!dateFrom || !dateTo) {
            showToast('Please select both start and end dates', 'error');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/videos/bulk-delete-by-date', {
                dateFrom,
                dateTo
            });
            await fetchVideos();
            showToast(`✅ ${response.data.deletedCount} videos deleted`);
            setDateFrom('');
            setDateTo('');
        } catch (error) {
            showToast('Error deleting videos by date', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Request password before delete
    const requestPassword = (action, message) => {
        setPendingAction(() => action);
        setShowPasswordModal(true);
    };

    return (
        <div>
            {/* Settings Form */}
            <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 6px rgba(0,0,0,.08)', marginBottom: '22px' }}>
                <h3>⚙️ Site Settings</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="fg">
                        <label>Site Name</label>
                        <input type="text" name="siteName" value={formData.siteName || ''} onChange={handleChange}
                            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                    </div>
                    <div className="fg">
                        <label>Tagline / Slogan</label>
                        <input type="text" name="slogan" value={formData.slogan || ''} onChange={handleChange}
                            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                    </div>
                    <div className="fg">
                        <label>Admin Email</label>
                        <input type="email" name="email" value={formData.email || ''} onChange={handleChange}
                            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                    </div>
                    <div className="fg">
                        <label>Breaking News Ticker</label>
                        <select name="ticker" value={formData.ticker || 'on'} onChange={handleChange}
                            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}>
                            <option value="on">ON</option>
                            <option value="off">OFF</option>
                        </select>
                    </div>
                    <div className="fg full" style={{ gridColumn: '1/-1' }}>
                        <label>Footer About Text</label>
                        <textarea name="footerAbout" value={formData.footerAbout || ''} onChange={handleChange} rows="4"
                            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}></textarea>
                    </div>
                    <div className="fg full" style={{ gridColumn: '1/-1' }}>
                        <label>Twitter/X URL</label>
                        <input type="url" name="twitter" value={formData.twitter || ''} onChange={handleChange}
                            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                    </div>
                    <div className="fg full" style={{ gridColumn: '1/-1' }}>
                        <label>Facebook URL</label>
                        <input type="url" name="facebook" value={formData.facebook || ''} onChange={handleChange}
                            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                    </div>
                    <div className="fg full" style={{ gridColumn: '1/-1' }}>
                        <label>YouTube URL</label>
                        <input type="url" name="youtube" value={formData.youtube || ''} onChange={handleChange}
                            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                    </div>
                    <div className="fg full" style={{ gridColumn: '1/-1' }}>
                        <label>Live Stream Embed URL</label>
                        <input type="url" name="stream" value={formData.stream || ''} onChange={handleChange}
                            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                    </div>
                </div>
                <button className="btn btn-red" style={{ marginTop: '18px' }} onClick={handleSave}>Save Settings</button>
            </div>

            {/* Bulk Delete Section - Articles */}
            <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 6px rgba(0,0,0,.08)', marginBottom: '22px' }}>
                <h3 style={{ color: 'var(--red)' }}>🗑️ Delete Articles</h3>
                <p style={{ fontSize: '13px', color: '#888', marginBottom: '15px' }}>⚠️ These actions require admin password and cannot be undone.</p>

                <div style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', gap: '15px', marginBottom: '15px', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input type="radio" name="deleteType" value="all" checked={deleteType === 'all'} onChange={() => setDeleteType('all')} />
                            All Articles
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input type="radio" name="deleteType" value="date-range" checked={deleteType === 'date-range'} onChange={() => setDeleteType('date-range')} />
                            Date Range
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input type="radio" name="deleteType" value="category" checked={deleteType === 'category'} onChange={() => setDeleteType('category')} />
                            By Category
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input type="radio" name="deleteType" value="status" checked={deleteType === 'status'} onChange={() => setDeleteType('status')} />
                            By Status
                        </label>
                    </div>

                    {deleteType === 'date-range' && (
                        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center', marginTop: '15px' }}>
                            <div>
                                <label>From Date</label>
                                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                                    style={{ padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: '5px', marginLeft: '10px' }} />
                            </div>
                            <div>
                                <label>To Date</label>
                                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                                    style={{ padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: '5px', marginLeft: '10px' }} />
                            </div>
                            <button
                                onClick={() => requestPassword(deleteArticlesByDate, `Delete all articles from ${dateFrom} to ${dateTo}?`)}
                                className="btn btn-outline"
                                style={{ borderColor: 'var(--red)', color: 'var(--red)' }}
                                disabled={!dateFrom || !dateTo}
                            >
                                Delete by Date Range
                            </button>
                        </div>
                    )}

                    {deleteType === 'category' && (
                        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center', marginTop: '15px' }}>
                            <select value={deleteCategory} onChange={(e) => setDeleteCategory(e.target.value)}
                                style={{ padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: '5px', minWidth: '150px' }}>
                                <option value="">Select Category</option>
                                <option>India</option><option>World</option><option>Politics</option><option>Business</option>
                                <option>Sports</option><option>Entertainment</option><option>Technology</option><option>Lifestyle</option>
                                <option>Jokes</option>
                            </select>
                            <button
                                onClick={() => requestPassword(deleteArticlesByCategory, `Delete all articles in category "${deleteCategory}"?`)}
                                className="btn btn-outline"
                                style={{ borderColor: 'var(--red)', color: 'var(--red)' }}
                                disabled={!deleteCategory}
                            >
                                Delete by Category
                            </button>
                        </div>
                    )}

                    {deleteType === 'status' && (
                        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center', marginTop: '15px' }}>
                            <select value={deleteStatus} onChange={(e) => setDeleteStatus(e.target.value)}
                                style={{ padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: '5px', minWidth: '150px' }}>
                                <option value="">Select Status</option>
                                <option value="live">Live</option>
                                <option value="draft">Draft</option>
                            </select>
                            <button
                                onClick={() => requestPassword(deleteArticlesByStatus, `Delete all ${deleteStatus} articles?`)}
                                className="btn btn-outline"
                                style={{ borderColor: 'var(--red)', color: 'var(--red)' }}
                                disabled={!deleteStatus}
                            >
                                Delete by Status
                            </button>
                        </div>
                    )}

                    {deleteType === 'all' && (
                        <div style={{ marginTop: '15px' }}>
                            <button
                                onClick={() => requestPassword(deleteAllArticles, 'Delete ALL articles? This will remove every article from the database.')}
                                className="btn btn-outline"
                                style={{ borderColor: 'var(--red)', color: 'var(--red)' }}
                            >
                                🗑️ Delete All Articles
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Bulk Delete Section - Videos */}
            <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 6px rgba(0,0,0,.08)', marginBottom: '22px' }}>
                <h3 style={{ color: 'var(--red)' }}>🗑️ Delete Videos</h3>
                <p style={{ fontSize: '13px', color: '#888', marginBottom: '15px' }}>⚠️ These actions require admin password and cannot be undone.</p>

                <div style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', gap: '15px', marginBottom: '15px', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input type="radio" name="deleteVideoType" value="all" checked={deleteVideoType === 'all'} onChange={() => setDeleteVideoType('all')} />
                            All Videos
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input type="radio" name="deleteVideoType" value="date-range" checked={deleteVideoType === 'date-range'} onChange={() => setDeleteVideoType('date-range')} />
                            Date Range
                        </label>
                    </div>

                    <div>
                        <button
                            onClick={() => requestPassword(deleteAllVideos, 'Delete ALL videos? This will remove every video from the database.')}
                            className="btn btn-outline"
                            style={{ borderColor: 'var(--red)', color: 'var(--red)' }}
                        >
                            🗑️ Delete All Videos
                        </button>
                    </div>

                    {deleteVideoType === 'date-range' && (
                        <div style={{ marginTop: '15px' }}>
                            <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
                                <div>
                                    <label>From Date</label>
                                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                                        style={{ padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: '5px', marginLeft: '10px' }} />
                                </div>
                                <div>
                                    <label>To Date</label>
                                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                                        style={{ padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: '5px', marginLeft: '10px' }} />
                                </div>
                                <button
                                    onClick={() => requestPassword(deleteVideosByDate, `Delete all videos from ${dateFrom} to ${dateTo}?`)}
                                    className="btn btn-outline"
                                    style={{ borderColor: 'var(--red)', color: 'var(--red)' }}
                                    disabled={!dateFrom || !dateTo}
                                >
                                    Delete Videos by Date Range
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Password Confirmation Modal */}
            {showPasswordModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: '12px',
                        padding: '28px',
                        maxWidth: '400px',
                        width: '90%',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '15px' }}>🔐</div>
                        <h3 style={{ color: 'var(--red)', marginBottom: '10px' }}>Admin Password Required</h3>
                        <p style={{ color: '#666', marginBottom: '20px' }}>
                            This action cannot be undone. Please enter your admin password to continue.
                        </p>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter admin password"
                            autoFocus
                            style={{
                                width: '100%',
                                padding: '12px',
                                border: '1.5px solid #ddd',
                                borderRadius: '8px',
                                marginBottom: '15px',
                                fontSize: '14px'
                            }}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    verifyPassword(pendingAction);
                                }
                            }}
                        />
                        {passwordError && (
                            <p style={{ color: 'var(--red)', fontSize: '13px', marginBottom: '15px' }}>{passwordError}</p>
                        )}
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <button
                                onClick={() => {
                                    setShowPasswordModal(false);
                                    setPassword('');
                                    setPasswordError('');
                                }}
                                className="btn btn-outline"
                                style={{ padding: '8px 20px' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => verifyPassword(pendingAction)}
                                className="btn btn-red"
                                style={{ padding: '8px 20px' }}
                            >
                                Confirm & Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading Overlay */}
            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000
                }}>
                    <div style={{ background: '#fff', padding: '30px', borderRadius: '12px', textAlign: 'center' }}>
                        <div className="loader-spinner" style={{ margin: '0 auto 15px' }}></div>
                        <p>Processing your request...</p>
                    </div>
                </div>
            )}

            {/* Change Password Section */}
            <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 6px rgba(0,0,0,.08)', marginBottom: '22px' }}>
                <h3>🔐 Admin Password</h3>

                {!showChangePassword ? (
                    <div>
                        <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                            Change your admin account password.
                        </p>
                        <button
                            className="btn btn-outline"
                            onClick={() => setShowChangePassword(true)}
                        >
                            Change Password
                        </button>
                    </div>
                ) : (
                    <div>
                        <div className="fg" style={{ marginBottom: '15px' }}>
                            <label>Current Password</label>
                            <input
                                type="password"
                                value={oldPassword}
                                onChange={(e) => setOldPassword(e.target.value)}
                                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                            />
                        </div>
                        <div className="fg" style={{ marginBottom: '15px' }}>
                            <label>New Password</label>
                            <input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                            />
                        </div>
                        <div className="fg" style={{ marginBottom: '15px' }}>
                            <label>Confirm New Password</label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button className="btn btn-red" onClick={handleChangePassword}>Save New Password</button>
                            <button className="btn btn-outline" onClick={() => setShowChangePassword(false)}>Cancel</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SettingsManager;